// One place for every KPI's label, unit and formula, so the cards, the chart and
// the definitions table can't drift apart. Values come from GET /api/kpi.

// Tolerates an API response from before the funnels existed.
const rf = (m, k) => (m.ratingFunnel ? m.ratingFunnel[k] : null);
const uf = (m, k) => (m.uploadFunnel ? m.uploadFunnel[k] : null);

export const METRICS = {
    acquisitionSource: {
        label: 'Acquisition Source',
        notTracked: 'Signup does not record a source or channel yet.'
    },
    activeUsers: {
        label: 'Active Users', unit: 'count', color: 'primary',
        get: (m) => m.activeUsers,
        definition: 'Distinct accounts that rated, commented, spotlighted, uploaded, played, liked or viewed a song, or tapped a boost link, in the period. Website page views are not included.'
    },
    newUsers: {
        label: 'New Users', unit: 'count', color: 'primary',
        get: (m) => m.newAccounts,
        definition: 'Accounts created in the period (all signups, as on the Dashboard). New users who were also active are included in Active Users.'
    },
    returningUsers: {
        label: 'Returning Users', unit: 'count', color: 'primary',
        get: (m) => m.returningUsers,
        definition: 'Active users whose account was created before the period started. Accounts with no recorded signup date count as existing; deleted accounts are excluded.'
    },
    spotlightUsage: {
        label: 'Spotlight Usage', unit: 'count', color: 'info',
        get: (m) => m.spotlightUsage,
        definition: 'Songs spotlighted (new spotlight records). Re-adding a removed spotlight is not counted again.'
    },
    boostUsage: {
        label: 'Boost Usage', unit: 'count', color: 'info',
        get: (m) => m.boostUsage,
        definition: "Taps on an artist's activation links from the Social Share page. Taps from the feed's boost sheet are not recorded yet, so this undercounts."
    },
    commentUsage: {
        label: 'Comment Usage', unit: 'count', color: 'info',
        get: (m) => m.commentUsage,
        definition: 'Comments on songs plus comments on spotlights.'
    },
    avgListeningSec: {
        label: 'Avg Listening Time / User', unit: 'sec', color: 'info',
        get: (m) => m.avgListeningSec,
        definition: 'Stand-in for engagement time: total listening time ÷ users who listened. A play is recorded when a song stops or ends, not when the app goes to the background.'
    },
    sessionTime: {
        label: 'Avg Session Time / User',
        notTracked: 'The app does not record sessions or time in the app yet.'
    },
    avgRatingsPerRater: {
        label: 'Avg Ratings / User', unit: 'ratio', color: 'info',
        get: (m) => m.avgRatingsPerRater,
        definition: 'Ratings submitted ÷ distinct users who rated in the period.'
    },
    retentionD1: {
        label: 'Retention D1', unit: '%', color: 'success',
        get: (m) => m.retention.d1.rate,
        detail: (m) => `${m.retention.d1.retained} of ${m.retention.d1.eligible} new users`,
        definition: 'Of accounts created in the period whose day 1 has fully passed, the % active on the day after signup (UTC days).'
    },
    retentionD7: {
        label: 'Retention D7', unit: '%', color: 'success',
        get: (m) => m.retention.d7.rate,
        detail: (m) => `${m.retention.d7.retained} of ${m.retention.d7.eligible} new users`,
        definition: 'Of accounts created in the period whose day 7 has fully passed, the % active exactly 7 days after signup.'
    },
    retentionD30: {
        label: 'Retention D30', unit: '%', color: 'success',
        get: (m) => m.retention.d30.rate,
        detail: (m) => `${m.retention.d30.retained} of ${m.retention.d30.eligible} new users`,
        definition: 'Of accounts created in the period whose day 30 has fully passed, the % active exactly 30 days after signup.'
    },
    churnRate: {
        label: 'Churn Rate', unit: '%', color: 'error', better: 'down',
        get: (m) => m.churnRate,
        detail: (m) => `${m.churned} users stopped`,
        definition: 'Of the users active in the previous period, the % not active in this period. Lower is better.'
    },
    ttfaAvgHours: {
        label: 'Time to First Action', unit: 'hours', color: 'warning', better: 'down',
        get: (m) => m.timeToFirstAction.avgHours,
        detail: (m) => (m.timeToFirstAction.medianHours === null ? '' : `median ${m.timeToFirstAction.medianHours} h`),
        definition: 'Average hours from signup to the first rating, upload, comment or spotlight, for accounts created in the period that have acted so far.'
    },
    activationRate: {
        label: 'Activation Rate', unit: '%', color: 'warning',
        get: (m) => m.timeToFirstAction.activationRate,
        detail: (m) => `${m.timeToFirstAction.activated} of ${m.newAccounts} new users`,
        definition: 'Of accounts created in the period, the % that have made a first rating, upload, comment or spotlight so far.'
    },
    ratingsPerUpload: {
        label: 'Upload-to-Rating Ratio', unit: 'ratio', color: 'primary',
        get: (m) => m.ratingsPerUpload,
        detail: (m) => `${m.ratings} ratings / ${m.uploads} uploads`,
        definition: 'Ratings submitted in the period ÷ songs uploaded in the period (ratings per upload).'
    },
    listenRate: {
        label: 'Play → 15s Listen', unit: '%', color: 'primary',
        get: (m) => rf(m, 'listenRate'),
        detail: (m) => `${rf(m, 'listened')} of ${rf(m, 'played')} song plays`,
        definition: "Of the songs each listener played in the period, the % they listened to for at least 15 seconds (the app's own listen threshold). Counted once per listener and song."
    },
    ratingConversion: {
        label: 'Listen → Rating', unit: '%', color: 'primary',
        get: (m) => rf(m, 'rateRate'),
        detail: (m) => `${rf(m, 'rated')} of ${rf(m, 'listened')} 15s listens`,
        definition: 'Of the songs listened to for 15 seconds or more in the period, the % that listener has rated.'
    },
    uploadConversion: {
        label: 'Signup → First Upload', unit: '%', color: 'primary',
        get: (m) => uf(m, 'uploadRate'),
        detail: (m) => `${uf(m, 'uploaded')} of ${uf(m, 'signedUp')} new accounts`,
        definition: 'Of accounts created in the period, the % that have uploaded at least one song so far.'
    },
    uploadRatedRate: {
        label: 'Upload → First Rating', unit: '%', color: 'primary',
        get: (m) => uf(m, 'ratedRate'),
        detail: (m) => `${uf(m, 'gotRated')} of ${uf(m, 'uploaded')} uploaders`,
        definition: "Of those new accounts that uploaded, the % whose song has been rated by someone else so far."
    },
    funnelEarlySteps: {
        label: 'Earlier funnel steps',
        notTracked: 'Opening a song, and upload attempts that fail or are abandoned, are not recorded yet, so the funnels start at "played" and "signed up".'
    },
    arpuUsd: {
        label: 'ARPU', unit: 'usd', color: 'success',
        get: (m) => m.arpuUsd,
        definition: 'Revenue ÷ active users in the period.'
    },
    revenueUsd: {
        label: 'Revenue', unit: 'usd', color: 'success',
        get: (m) => m.revenueUsd,
        definition: "Token purchases (credited, not refunded) at USD list price, before Apple's or Google's cut. In-app purchases started on 20 Aug 2026."
    },
    payingUsers: {
        label: 'Paying Users', unit: 'count', color: 'success',
        get: (m) => m.payingUsers,
        definition: 'Distinct users with a credited token purchase in the period.'
    }
};

// Grouped as in the KPI request. New users, ratings and uploads live on the Dashboard.
// Four headline numbers, then short grouped lists; not-tracked metrics get one line.
export const HEADLINE = ['activeUsers', 'newUsers', 'returningUsers', 'churnRate', 'revenueUsd'];

export const SECTIONS = [
    { title: 'Engagement', keys: ['spotlightUsage', 'boostUsage', 'commentUsage', 'avgRatingsPerRater', 'avgListeningSec'] },
    { title: 'Retention & Activation', keys: ['retentionD1', 'retentionD7', 'retentionD30', 'activationRate', 'ttfaAvgHours'] },
    { title: 'Content & Monetization', keys: ['ratingsPerUpload', 'arpuUsd', 'payingUsers'] }
];

// Drop-off funnels built from recorded steps only; see funnelEarlySteps.
export const FUNNELS = [
    {
        title: 'Rating funnel',
        subtitle: 'per listener and song',
        hint: 'Each listener + song pair counts once in the period: one person playing 10 different songs counts 10; replaying the same song adds nothing. The % is the share of the step above.',
        steps: [
            { label: 'Played', get: (m) => rf(m, 'played'),
              hint: 'Listener + song pairs with at least one recorded play in the period. A play is recorded when the song stops or finishes.' },
            { label: 'Listened 15s+', get: (m) => rf(m, 'listened'), rateKey: 'listenRate',
              hint: "Of those, the ones the listener played for 15 seconds or more: the app's own listen threshold before a rating counts." },
            { label: 'Rated', get: (m) => rf(m, 'rated'), rateKey: 'ratingConversion',
              hint: 'Of the 15s+ listens, the ones where that listener has rated that song.' }
        ]
    },
    {
        title: 'Upload funnel',
        subtitle: 'new accounts in the period',
        hint: 'Follows the accounts created in the period through their first upload. The % is the share of the step above.',
        steps: [
            { label: 'Signed up', get: (m) => uf(m, 'signedUp'),
              hint: 'Accounts created in the period (the same number as New Users).' },
            { label: 'Uploaded a song', get: (m) => uf(m, 'uploaded'), rateKey: 'uploadConversion',
              hint: 'Of those, the accounts that have uploaded at least one song so far.' },
            { label: 'Song rated by others', get: (m) => uf(m, 'gotRated'), rateKey: 'uploadRatedRate',
              hint: 'Of the uploaders, the accounts whose song has been rated by someone other than themselves so far.' }
        ]
    }
];

export const NOT_TRACKED = ['acquisitionSource', 'sessionTime', 'funnelEarlySteps'];

export function formatValue(unit, v) {
    if (v === null || v === undefined) return '—';
    switch (unit) {
        case '%': return `${v}%`;
        case 'sec': return v >= 60 ? `${Math.floor(v / 60)}m ${Math.round(v % 60)}s` : `${Math.round(v)}s`;
        case 'hours': return v >= 48 ? `${Math.round((v / 24) * 10) / 10} d` : `${v} h`;
        case 'ratio': return Number(v).toFixed(2);
        case 'usd': return `$${Number(v).toFixed(2)}`;
        default: return Number(v).toLocaleString();
    }
}

// Rates change in points; everything else in percent of the previous value.
export function changeOf(def, current, previous) {
    const v = current === undefined ? null : current;
    const p = previous === undefined ? null : previous;
    if (v === null || p === null) return null;
    if (def.unit === '%') return { kind: 'pts', value: Math.round((v - p) * 10) / 10 };
    if (p === 0) return v === 0 ? { kind: 'pct', value: 0 } : null;
    return { kind: 'pct', value: Math.round(((v - p) / p) * 1000) / 10 };
}
